/**
 * The strip beneath the chart: zoom controls, a jump back to the latest bar,
 * and a live clock in exchange time.
 *
 * The clock is in America/New_York rather than the viewer's zone on purpose.
 * Every session boundary this platform cares about -- the 09:30 open, the
 * 16:00 close, the pre- and post-market windows -- is defined in exchange
 * time, so a clock in local time makes the reader do the conversion at
 * exactly the moment they are least inclined to.
 */
import { useEffect, useState } from "react";
import type { IChartApi } from "lightweight-charts";

import { Brand } from "./Brand";

/** Bar spacing bounds. Below ~1.5px candles stop being distinguishable; above
 *  ~60px a handful of bars fill the screen and the chart stops being one. */
const MIN_SPACING = 1.5;
const MAX_SPACING = 60;
const ZOOM_STEP = 1.25;

export type SessionPhase = "pre" | "open" | "post" | "closed";

export interface MarketClock {
  /** HH:MM:SS in exchange time. */
  time: string;
  /** EST or EDT, whichever New York is actually on today. */
  zone: string;
  phase: SessionPhase;
  label: string;
}

const PHASE_LABEL: Record<SessionPhase, string> = {
  pre: "Pre-market",
  open: "Market open",
  post: "After hours",
  closed: "Closed",
};

/** Exchange-time parts for an instant, without pulling in a date library. */
function exchangeParts(
  d: Date,
): { h: number; m: number; s: number; weekday: string; zone: string } {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    weekday: "short",
    // Asked for rather than hardcoded: New York is EST for four months and
    // EDT for eight. A label fixed at "EST" is wrong for two thirds of the
    // year, and wrong in the direction that matters -- an hour off, on the
    // clock the session boundaries are defined against.
    timeZoneName: "short",
  });
  const parts: Record<string, string> = {};
  for (const part of fmt.formatToParts(d)) parts[part.type] = part.value;
  // 24:00 is a legal formatToParts result for midnight in some engines.
  const h = Number(parts.hour ?? "0") % 24;
  return {
    h,
    m: Number(parts.minute ?? "0"),
    s: Number(parts.second ?? "0"),
    weekday: parts.weekday ?? "",
    zone: parts.timeZoneName ?? "ET",
  };
}

export function marketClock(now: Date): MarketClock {
  const { h, m, s, weekday, zone } = exchangeParts(now);
  const mins = h * 60 + m;
  const pad = (n: number) => String(n).padStart(2, "0");
  const time = `${pad(h)}:${pad(m)}:${pad(s)}`;

  // Weekends are closed regardless of the clock. Exchange holidays are not
  // handled here: the calendar lives on the server, and a clock that silently
  // guessed wrong about Good Friday would be worse than one that says "open"
  // and is contradicted by the absence of ticks.
  let phase: SessionPhase = "closed";
  if (weekday !== "Sat" && weekday !== "Sun") {
    if (mins >= 4 * 60 && mins < 9 * 60 + 30) phase = "pre";
    else if (mins >= 9 * 60 + 30 && mins < 16 * 60) phase = "open";
    else if (mins >= 16 * 60 && mins < 20 * 60) phase = "post";
  }
  return { time, zone, phase, label: PHASE_LABEL[phase] };
}

function zoom(chart: IChartApi | null, factor: number) {
  if (!chart) return;
  const ts = chart.timeScale();
  const current = ts.options().barSpacing ?? 7;
  const next = Math.min(MAX_SPACING, Math.max(MIN_SPACING, current * factor));
  ts.applyOptions({ barSpacing: next });
}

export interface ChartFooterProps {
  chart: IChartApi | null;
  /** Shown on the left, e.g. "AAPL · 5m". */
  caption?: string;
  /** Following today's session, refreshed on a timer. */
  live?: boolean;
  /** Absent means the host does not offer live mode; the button is then hidden. */
  onToggleLive?: () => void;
}

export function ChartFooter({ chart, caption, live = false, onToggleLive }: ChartFooterProps) {
  const [clock, setClock] = useState<MarketClock>(() => marketClock(new Date()));

  useEffect(() => {
    // Aligned to the next whole second so the display does not sit visibly
    // behind a wall clock.
    let timer: number;
    const tick = () => {
      setClock(marketClock(new Date()));
      timer = window.setTimeout(tick, 1000 - (Date.now() % 1000));
    };
    timer = window.setTimeout(tick, 1000 - (Date.now() % 1000));
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div className="chart-footer">
      {/* In the footer rather than over the plot. Anywhere inside the price
          pane sits on top of candles at some scroll position or other, which
          forces the mark down to an opacity where it is barely there. Out
          here there is nothing behind it, so it can be fully opaque. */}
      <Brand size={17} markOnly className="cf-brand" />
      {caption && <span className="cf-caption">{caption}</span>}

      <div className="cf-spacer" />

      {onToggleLive && (
        <button
          className={`cf-btn cf-live ${live ? "on" : ""}`}
          aria-pressed={live}
          title={
            live
              ? "Stop following. Puts the timeframe back where it was."
              : "Follow today: 5-minute bars, newest on screen, refreshed every 15s"
          }
          onClick={() => {
            onToggleLive();
            // Going live should land on the newest bar, not wherever the
            // chart happened to be scrolled.
            if (!live) setTimeout(() => chart?.timeScale().scrollToRealTime(), 60);
          }}
        >
          <i className="cf-live-dot" />
          Live
        </button>
      )}
      <button
        className="cf-btn"
        title="Jump to the most recent bar"
        onClick={() => chart?.timeScale().scrollToRealTime()}
      >
        Today
      </button>
      <button
        className="cf-btn"
        title="Fit every bar on screen"
        onClick={() => chart?.timeScale().fitContent()}
      >
        Fit
      </button>

      <div className="cf-zoom">
        <button
          className="cf-btn cf-icon"
          title="Zoom out"
          aria-label="Zoom out"
          onClick={() => zoom(chart, 1 / ZOOM_STEP)}
        >
          −
        </button>
        <button
          className="cf-btn cf-icon"
          title="Zoom in"
          aria-label="Zoom in"
          onClick={() => zoom(chart, ZOOM_STEP)}
        >
          +
        </button>
      </div>

      <span
        className={`cf-clock ${clock.phase}`}
        title={`${clock.label} · New York (${clock.zone})`}
      >
        <span className="cf-dot" />
        {clock.time}
        <span className="cf-tz">{clock.zone}</span>
      </span>
    </div>
  );
}
